if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.currentVariantId = null;
        this.variantImagesEnabled = this.dataset.variantImagesEnabled === 'true';
        
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.bindThumbnailEvents();
        
        // Listen for variant changes
        if (this.variantImagesEnabled) {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
        }
        
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      bindThumbnailEvents() {
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
      }

      onVariantChange({ data }) {
        if (!data || !data.variant) return;

        const newVariantId = data.variant.id;
        if (this.currentVariantId === newVariantId) return;

        this.currentVariantId = newVariantId;
        this.updateVariantImages(newVariantId);
      }

      updateVariantImages(variantId) {
        // Hide variant metafield images that don't belong to current variant
        const variantImages = this.querySelectorAll('[data-variant-id]');
        variantImages.forEach(image => {
          const imageVariantId = image.dataset.variantId;
          if (imageVariantId && imageVariantId !== variantId.toString()) {
            image.style.display = 'none';
            image.classList.add('variant-hidden');
          } else if (imageVariantId === variantId.toString()) {
            image.style.display = '';
            image.classList.remove('variant-hidden');
          }
        });

        // Update thumbnail visibility
        const variantThumbnails = this.elements.thumbnails?.querySelectorAll('[data-variant-id]');
        variantThumbnails?.forEach(thumbnail => {
          const thumbnailVariantId = thumbnail.dataset.variantId;
          if (thumbnailVariantId && thumbnailVariantId !== variantId.toString()) {
            thumbnail.style.display = 'none';
            thumbnail.classList.add('variant-hidden');
          } else if (thumbnailVariantId === variantId.toString()) {
            thumbnail.style.display = '';
            thumbnail.classList.remove('variant-hidden');
          }
        });

        // Rebind thumbnail events for newly shown images
        this.bindThumbnailEvents();

        // Reset slider if it exists
        if (this.elements.viewer.slider) {
          this.elements.viewer.resetPages();
        }
        if (this.elements.thumbnails?.slider) {
          this.elements.thumbnails.resetPages();
        }

        // Switch to first available image for this variant if current is hidden
        const currentActiveMedia = this.elements.viewer.querySelector('.is-active');
        if (currentActiveMedia && currentActiveMedia.classList.contains('variant-hidden')) {
          const firstVisibleMedia = this.elements.viewer.querySelector('[data-media-id]:not(.variant-hidden)');
          if (firstVisibleMedia) {
            this.setActiveMedia(firstVisibleMedia.dataset.mediaId, false);
          }
        }
      }
    }
  );
}
